import { Form } from "react-router-dom";
import { useIntl } from "react-intl";
import styles from "./SearchTools.module.scss";
import SearchComboBox from "../../../../common/core-ui/SearchComboBox.tsx";
import { useRef, useState } from "react";
import useTypeahead from "../typeahead/useTypeahead.ts";
import useSampleTypeahead from "../typeahead/useSampleTypeahead.ts";
import TypeaheadAsset from "../typeahead/TypeaheadAsset.tsx";
import TypeaheadDirectSearch from "../typeahead/TypeaheadDirectSearch.tsx";
import { SearchLink, AssetLink } from "../typeahead/types.ts";
import { clsx } from "clsx";
import useReversePortalRefocus from "../../../../common/core-ui/useReversePortalRefocus.ts";
import { isThemeBackgroundDark } from "routes/root/core-ui/initTheme.ts";
import useSampleSearchEligibility from "./useSampleSearchEligibility.ts";

export type LayoutMode = "at-top" | "full-size";

// Type guard for AssetLink
function isAssetLink(item: SearchLink | AssetLink): item is AssetLink {
  return "asset" in item;
}

interface BaseTypeaheadProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClear: () => void;
  labelText?: string;
  inlineLabel?: boolean;
}

interface SharedComboBoxProps extends BaseTypeaheadProps {
  recentSearches: SearchLink[];
  typeaheadMatches: (SearchLink | AssetLink)[];
  onSelection: (index: number) => void;
}

// Shared component with all common props
function SharedComboBox({
  value,
  onChange,
  onClear,
  labelText,
  inlineLabel,
  recentSearches,
  typeaheadMatches,
  onSelection,
}: SharedComboBoxProps) {
  const intl = useIntl();

  return (
    <SearchComboBox
      noBorder={isThemeBackgroundDark()}
      onSelectionMade={onSelection}
      placeholder={intl.formatMessage({
        defaultMessage: "Search by ISBN, title, author or publisher...",
      })}
      labelText={labelText}
      inlineLabel={inlineLabel}
      value={value}
      onChange={onChange}
      onClear={onClear}
      name="q"
      inputSize="large"
      listTitle={
        value === ""
          ? intl.formatMessage({ defaultMessage: "Recent Searches" })
          : undefined
      }
      listItems={
        value === ""
          ? recentSearches.map((item) => ({
              key: item.term,
              text: item.term,
            }))
          : typeaheadMatches.map((item) => {
              if (isAssetLink(item)) {
                return {
                  key: item.asset.manageAsset.vbid,
                  element: (
                    <TypeaheadAsset
                      asset={item.asset.manageAsset}
                      query={item.term}
                      matchType={item.matchType}
                    />
                  ),
                };
              } else {
                return {
                  key: item.type,
                  element: <TypeaheadDirectSearch {...item} />,
                };
              }
            })
      }
    />
  );
}

function LibraryTypeaheadComboBox({
  value,
  recentSearches,
  ...props
}: BaseTypeaheadProps & { recentSearches: SearchLink[] }) {
  const typeaheadMatches = useTypeahead(value);

  const handleSelection = (index: number) => {
    const items = value === "" ? recentSearches : typeaheadMatches;
    items[index].action();
  };

  return (
    <SharedComboBox
      value={value}
      recentSearches={recentSearches}
      typeaheadMatches={typeaheadMatches}
      onSelection={handleSelection}
      {...props}
    />
  );
}

function SampleTypeaheadComboBox({
  value,
  recentSearches,
  ...props
}: BaseTypeaheadProps & { recentSearches: SearchLink[] }) {
  const typeaheadMatches = useSampleTypeahead(value);

  const handleSelection = (index: number) => {
    const items = value === "" ? recentSearches : typeaheadMatches;
    items[index].action();
  };

  return (
    <SharedComboBox
      value={value}
      recentSearches={recentSearches}
      typeaheadMatches={typeaheadMatches}
      onSelection={handleSelection}
      {...props}
    />
  );
}

export default function SearchTools({
  query,
  recentSearches,
  displayMode,
  activeTab,
}: {
  query?: string;
  recentSearches: SearchLink[];
  displayMode: LayoutMode;
  activeTab: "library" | "sample";
}) {
  const intl = useIntl();
  const formRef = useRef<HTMLFormElement>(null);
  const [value, setValue] = useState(query ?? "");
  const { isSampleSearchEnabled } = useSampleSearchEligibility();

  const containerRef = useRef(null);
  useReversePortalRefocus(containerRef.current);

  // Compute label text and inline label based on display mode and active tab
  const labelText =
    displayMode === "at-top" && isSampleSearchEnabled
      ? activeTab === "sample"
        ? intl.formatMessage({ defaultMessage: "Sample" })
        : intl.formatMessage({ defaultMessage: "My Library" })
      : undefined;

  const inlineLabel = displayMode === "at-top" && isSampleSearchEnabled;

  const TypeaheadComboBox =
    activeTab === "sample" ? SampleTypeaheadComboBox : LibraryTypeaheadComboBox;

  return (
    <div
      className={clsx(displayMode === "at-top" && styles.atTop)}
      ref={containerRef}
    >
      <Form
        method="GET"
        className={styles.form}
        ref={formRef}
        action={`/search/${activeTab}/titles`}
      >
        <TypeaheadComboBox
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onClear={() => setValue("")}
          recentSearches={recentSearches}
          labelText={labelText}
          inlineLabel={inlineLabel}
        />
      </Form>
    </div>
  );
}
